import React, { useState, useEffect, useContext } from "react";
import DeliveryWay from "./DeliveryWay";
import { LocalizationContext } from '../../localization/LocalizationContext';
import { parseMultiLanguageDescription } from '../../utils/multiLanguageParser';
import { isRestaurantOpen, getOpeningHoursMessage } from '../../utils/timeUtils';
import { getMetaBySyrveKey } from '../../config/restaurantMeta';
import { POS_CONFIG } from '../../config/posConfig';
import { API_ENDPOINTS, API_PROXY_BASE } from '../../config/api';
import { isDeliveryMode } from '../../utils/orderMode';
import { useTranslation } from 'react-i18next';
import '../../css/Bascket.css';

const isEuropeSquareDomain = () => {
    const hostname = window.location.hostname;
    return hostname === 'europesquare.ge' ||
           hostname === 'www.europesquare.ge';
};

const Bascket = ({ handleXBarItemChange }) => {
    const { translations, locale } = useContext(LocalizationContext);
    const { t } = useTranslation();
    const [selectedProducts, setSelectedProducts] = useState({});
    const [productQuantities, setProductQuantities] = useState({});
    const [totalPrice, setTotalPrice] = useState(0);
    const [isOrdering, setIsOrdering] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [isEuropeSquare, setIsEuropeSquare] = useState(false);
    const [openDotsMenu, setOpenDotsMenu] = useState(null);
    const [stopListBanner, setStopListBanner] = useState(null);
    const [checkingStopList, setCheckingStopList] = useState(false);

    const getTranslatedField = (text) => {
        if (!text) return '';
        const translated = parseMultiLanguageDescription(text, locale);
        return translated || text;
    };

    useEffect(() => {
        setIsOpen(isRestaurantOpen());
        setIsEuropeSquare(isEuropeSquareDomain());

        const storedProducts = JSON.parse(localStorage.getItem("cartByCompany")) || {};
        const storedQuantities = JSON.parse(localStorage.getItem("productQuantities")) || {};
        setSelectedProducts(storedProducts);
        setProductQuantities(storedQuantities);
    }, []);

    useEffect(() => {
        let sub = 0;
        let totalDeliveryFees = 0;
        Object.keys(selectedProducts).forEach(orgId => {
            const meta = getMetaBySyrveKey(orgId);
            if (meta && selectedProducts[orgId].length > 0) {
                totalDeliveryFees += meta.deliveryFee || 0;
            }
            selectedProducts[orgId].forEach(product => {
                const quantity = productQuantities[product.id] || 1;
                const modifiersTotal = (product.modifiers || []).reduce(
                    (sum, mod) => sum + (mod.price || 0) * (mod.amount || 1),
                    0
                );
                sub += (product.price + modifiersTotal) * quantity;
            });
        });

        const finalTotal = sub + totalDeliveryFees;
        setTotalPrice(Number(finalTotal.toFixed(2)));
    }, [selectedProducts, productQuantities]);

    const syncToLocalStorage = (products, quantities) => {
        localStorage.setItem("cartByCompany", JSON.stringify(products));
        localStorage.setItem("productQuantities", JSON.stringify(quantities));
    };

    const handleRemove = (productId, organizationId) => {
        const updatedProducts = { ...selectedProducts };
        updatedProducts[organizationId] = updatedProducts[organizationId].filter(p => p.id !== productId);
        if (updatedProducts[organizationId].length === 0) {
            delete updatedProducts[organizationId];
        }

        const updatedQuantities = { ...productQuantities };
        delete updatedQuantities[productId];

        setSelectedProducts(updatedProducts);
        setProductQuantities(updatedQuantities);
        syncToLocalStorage(updatedProducts, updatedQuantities);
    };

    const handleRemoveRestaurant = (orgId) => {
        const updatedProducts = { ...selectedProducts };
        const removedItems = updatedProducts[orgId] || [];
        delete updatedProducts[orgId];

        const updatedQuantities = { ...productQuantities };
        removedItems.forEach(item => {
            delete updatedQuantities[item.id];
        });

        setSelectedProducts(updatedProducts);
        setProductQuantities(updatedQuantities);
        syncToLocalStorage(updatedProducts, updatedQuantities);
        setOpenDotsMenu(null);
    };

    const handleClearAll = () => {
        setSelectedProducts({});
        setProductQuantities({});
        syncToLocalStorage({}, {});
    };

    const increaseQuantity = (productId) => {
        setProductQuantities(prev => {
            const updated = { ...prev, [productId]: (prev[productId] || 1) + 1 };
            localStorage.setItem("productQuantities", JSON.stringify(updated));
            return updated;
        });
    };

    const decreaseQuantity = (productId, organizationId) => {
        const currentQty = productQuantities[productId] || 1;
        if (currentQty <= 1) {
            handleRemove(productId, organizationId);
            return;
        }
        setProductQuantities(prev => {
            const updated = { ...prev, [productId]: prev[productId] - 1 };
            localStorage.setItem("productQuantities", JSON.stringify(updated));
            return updated;
        });
    };

    const getModifierNames = (modifiers) => {
        if (!modifiers || modifiers.length === 0) return '';
        const names = modifiers
            .filter(m => m.name)
            .map(m => getTranslatedField(m.name));
        return names.join(', ');
    };

    const getTotalItemCount = () => {
        let count = 0;
        Object.values(selectedProducts).forEach(products => {
            products.forEach(product => {
                count += productQuantities[product.id] || 1;
            });
        });
        return count;
    };

    const getRestaurantSubtotal = (products) => {
        let sub = 0;
        products.forEach(product => {
            const quantity = productQuantities[product.id] || 1;
            const modifiersTotal = (product.modifiers || []).reduce(
                (sum, mod) => sum + (mod.price || 0) * (mod.amount || 1),
                0
            );
            sub += (product.price + modifiersTotal) * quantity;
        });
        return sub;
    };

    const getRestaurantItemCount = (products) => {
        return products.reduce((sum, product) => sum + (productQuantities[product.id] || 1), 0);
    };

    const fetchStoppedItemIds = async () => {
        try {
            const tokenRes = await fetch(API_ENDPOINTS.proxy.accessToken, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const { token } = await tokenRes.json();

            const res = await fetch(`${API_PROXY_BASE}/stop-lists`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ token, organizationIds: [POS_CONFIG.ALL.organizationId] })
            });
            if (!res.ok) return new Set();
            const data = await res.json();
            const stopped = new Set();
            (data.terminalGroupStopLists || []).forEach(orgEntry => {
                (orgEntry.items || []).forEach(tg => {
                    (tg.items || []).forEach(it => {
                        if (it.productId) stopped.add(it.productId);
                    });
                });
            });
            return stopped;
        } catch (e) {
            console.error('Stop-list fetch failed:', e);
            return new Set();
        }
    };

    const handleCheckoutClick = async () => {
        if (!isDeliveryMode()) return;

        setCheckingStopList(true);
        try {
            const stoppedIds = await fetchStoppedItemIds();
            if (stoppedIds.size === 0) {
                setIsOrdering(true);
                return;
            }

            const removed = [];
            const updatedProducts = { ...selectedProducts };
            const updatedQuantities = { ...productQuantities };

            Object.keys(updatedProducts).forEach(orgKey => {
                const kept = [];
                updatedProducts[orgKey].forEach(product => {
                    if (stoppedIds.has(product.id)) {
                        removed.push(getTranslatedField(product.name) || product.name);
                        delete updatedQuantities[product.id];
                    } else {
                        kept.push(product);
                    }
                });
                if (kept.length === 0) {
                    delete updatedProducts[orgKey];
                } else {
                    updatedProducts[orgKey] = kept;
                }
            });

            if (removed.length > 0) {
                setSelectedProducts(updatedProducts);
                setProductQuantities(updatedQuantities);
                syncToLocalStorage(updatedProducts, updatedQuantities);
                setStopListBanner(removed);
                return;
            }

            setIsOrdering(true);
        } finally {
            setCheckingStopList(false);
        }
    };

    if (isOrdering && totalPrice !== undefined) {
        return <DeliveryWay onBack={() => setIsOrdering(false)} totalPrice={totalPrice} selectedProducts={selectedProducts} productQuantities={productQuantities} />;
    }

    const restaurantKeys = Object.keys(selectedProducts).filter(k => selectedProducts[k].length > 0);
    const restaurantCount = restaurantKeys.length;
    const totalItemCount = getTotalItemCount();
    const hasItems = restaurantCount > 0;

    const getCheckoutDisabled = () => {
        if (isEuropeSquare) return true;
        if (!isDeliveryMode()) return true;
        if (!isOpen) return true;
        return false;
    };

    const getDisabledMessage = () => {
        if (isEuropeSquare) {
            return translations?.deliveryPage?.checkoutDisabled ?? "Online ordering is not available on this platform";
        }
        if (!isDeliveryMode()) {
            return translations?.deliveryPage?.orderingRequiresDelivery ?? "Ordering is available in Delivery mode only";
        }
        if (!isOpen) {
            return `${translations?.deliveryPage?.offlineMessage ?? "Offline"} — ${translations?.deliveryPage?.onlineHours ?? "Online only during"}: ${getOpeningHoursMessage()}`;
        }
        return '';
    };

    return (
        <div className="fd-cart">
            <div className="fd-cart-header">
                <button className="fd-cart-back-btn" onClick={() => handleXBarItemChange ? handleXBarItemChange(1) : window.history.back()}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>
                <span className="fd-cart-title">
                    {t('My Cart')} {hasItems && `(${totalItemCount})`}
                </span>
                {hasItems ? (
                    <button className="fd-cart-clear-btn" onClick={handleClearAll}>
                        {t('Clear All')}
                    </button>
                ) : (
                    <div style={{ width: 40 }} />
                )}
            </div>

            {!hasItems ? (
                <div className="fd-cart-empty">
                    <div className="fd-cart-empty-circle">
                        <svg className="fd-cart-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="8" cy="21" r="1" />
                            <circle cx="19" cy="21" r="1" />
                            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                        </svg>
                        <span className="fd-cart-empty-spark fd-cart-empty-spark--1" />
                        <span className="fd-cart-empty-spark fd-cart-empty-spark--2" />
                        <span className="fd-cart-empty-spark fd-cart-empty-spark--3" />
                    </div>
                    <h3 className="fd-cart-empty-title">
                        {t('Your cart is empty')}
                    </h3>
                    <p className="fd-cart-empty-subtitle">
                        {t("Looks like you haven't added anything yet.")}
                    </p>
                    <div className="fd-cart-empty-dots">
                        <span className="fd-cart-empty-dot" />
                        <span className="fd-cart-empty-dot fd-cart-empty-dot--active" />
                        <span className="fd-cart-empty-dot" />
                    </div>
                </div>
            ) : (
                <>
                    {restaurantKeys.map(orgId => {
                        const products = selectedProducts[orgId];
                        const meta = getMetaBySyrveKey(orgId);
                        const restaurantName = meta?.name || orgId;
                        const restaurantLogo = meta?.logo;
                        const deliveryTime = meta?.time;
                        const deliveryFee = meta?.deliveryFee ?? 0;
                        const restSubtotal = getRestaurantSubtotal(products);
                        const restItemCount = getRestaurantItemCount(products);

                        const deliveryFeeText = deliveryFee === 0
                            ? t('Free delivery')
                            : `₾${deliveryFee.toFixed(2)} ${t('delivery')}`;

                        return (
                            <div key={orgId} className="fd-cart-restaurant">
                                <div className="fd-cart-restaurant-header">
                                    {restaurantLogo ? (
                                        <img src={restaurantLogo} alt={restaurantName} className="fd-cart-restaurant-logo" />
                                    ) : (
                                        <div className="fd-cart-restaurant-logo" style={{ background: '#f0f0f0' }} />
                                    )}
                                    <div className="fd-cart-restaurant-info">
                                        <div className="fd-cart-restaurant-name">{restaurantName}</div>
                                        <div className="fd-cart-restaurant-meta">
                                            {deliveryTime && <>{deliveryTime} min</>}
                                            {deliveryTime && <> &bull; </>}
                                            {deliveryFeeText}
                                        </div>
                                    </div>
                                    <div className="fd-cart-dots-wrapper">
                                        <button
                                            className="fd-cart-dots-btn"
                                            onClick={() => setOpenDotsMenu(openDotsMenu === orgId ? null : orgId)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#666">
                                                <circle cx="12" cy="5" r="2" />
                                                <circle cx="12" cy="12" r="2" />
                                                <circle cx="12" cy="19" r="2" />
                                            </svg>
                                        </button>
                                        {openDotsMenu === orgId && (
                                            <>
                                                <div className="fd-cart-dots-overlay" onClick={() => setOpenDotsMenu(null)} />
                                                <div className="fd-cart-dots-menu">
                                                    <button
                                                        className="fd-cart-dots-menu-item"
                                                        onClick={() => handleRemoveRestaurant(orgId)}
                                                    >
                                                        {t('Remove all')}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {products.map(product => {
                                    const quantity = productQuantities[product.id] || 1;
                                    const modifiersTotal = (product.modifiers || []).reduce(
                                        (sum, mod) => sum + (mod.price || 0) * (mod.amount || 1),
                                        0
                                    );
                                    const itemTotal = (product.price + modifiersTotal) * quantity;
                                    const unitPrice = product.price + modifiersTotal;
                                    const modifierNames = getModifierNames(product.modifiers);

                                    return (
                                        <div key={product.id} className="fd-cart-item">
                                            {product.imageUrl ? (
                                                <img src={product.imageUrl} alt={product.title} className="fd-cart-item-img" />
                                            ) : (
                                                <div className="fd-cart-item-img-placeholder">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                                        <path d="m21 15-5-5L5 21" />
                                                    </svg>
                                                </div>
                                            )}
                                            <div className="fd-cart-item-body">
                                                <div className="fd-cart-item-top-row">
                                                    <div className="fd-cart-item-info">
                                                        <div className="fd-cart-item-name">{getTranslatedField(product.title)}</div>
                                                        {modifierNames && (
                                                            <div className="fd-cart-item-modifiers">{modifierNames}</div>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="fd-cart-remove-btn"
                                                        onClick={() => handleRemove(product.id, orgId)}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 6L6 18" />
                                                            <path d="M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div className="fd-cart-item-bottom-row">
                                                    <div>
                                                        <span className="fd-cart-item-price">₾{itemTotal.toFixed(2)}</span>
                                                        {quantity > 1 && (
                                                            <span className="fd-cart-item-unit-price">₾{unitPrice.toFixed(2)} each</span>
                                                        )}
                                                    </div>
                                                    <div className="fd-cart-qty">
                                                        <button className="fd-cart-qty-btn" onClick={() => decreaseQuantity(product.id, orgId)}>&#8722;</button>
                                                        <span className="fd-cart-qty-value">{quantity}</span>
                                                        <button className="fd-cart-qty-btn" onClick={() => increaseQuantity(product.id)}>+</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="fd-cart-subtotal">
                                    <span>
                                        {t('Subtotal')} ({restItemCount} {restItemCount === 1 ? t('item') : t('items')})
                                    </span>
                                    <span className="fd-cart-subtotal-amount">₾{restSubtotal.toFixed(2)}</span>
                                </div>
                            </div>
                        );
                    })}

                </>
            )}

            {hasItems && (() => {
                const subtotal = restaurantKeys.reduce((sum, k) => sum + getRestaurantSubtotal(selectedProducts[k]), 0);
                return (
                <aside className="fd-cart-sidebar">
                    <div className="fd-cart-savings">
                        <div className="fd-cart-savings-icon">👑</div>
                        <div>
                            <div className="fd-cart-savings-top">{t("You're saving with")}</div>
                            <div className="fd-cart-savings-brand">Fastdelivery</div>
                        </div>
                    </div>

                    <div className="fd-cart-summary">
                        <div className="fd-cart-summary-row">
                            <span>{t('Subtotal')}</span>
                            <span>₾{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="fd-cart-summary-row">
                            <span>{t('Delivery Fee')} <span className="fd-cart-info-dot">i</span></span>
                            <span>₾{(totalPrice - subtotal).toFixed(2)}</span>
                        </div>
                        <div className="fd-cart-summary-row">
                            <span>{t('Service Fee')} <span className="fd-cart-info-dot">i</span></span>
                            <span>₾0.00</span>
                        </div>
                        <div className="fd-cart-summary-divider" />
                        <div className="fd-cart-summary-total">
                            <span>{t('Total (incl. fees)')}</span>
                            <span className="fd-cart-summary-total-value">₾{totalPrice.toFixed(2)}</span>
                        </div>
                    </div>

                    {stopListBanner && stopListBanner.length > 0 && (
                        <div className="fd-cart-stoplist-banner">
                            <strong>{t('Removed from cart')}:</strong> {stopListBanner.join(', ')}
                            <div className="fd-cart-stoplist-note">{t('These items are temporarily not available.')}</div>
                            <button className="fd-cart-stoplist-dismiss" onClick={() => setStopListBanner(null)}>×</button>
                        </div>
                    )}

                    <button
                        className="fd-cart-checkout-btn"
                        disabled={getCheckoutDisabled() || checkingStopList}
                        onClick={handleCheckoutClick}
                    >
                        {checkingStopList ? t('Checking...') : t('Checkout')} <span className="fd-cart-checkout-arrow">&rarr;</span>
                    </button>

                    <div className="fd-cart-secure">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <span>{t('Secure Checkout')}</span>
                    </div>

                    {getCheckoutDisabled() && (
                        <div className="fd-cart-disabled-message">
                            {getDisabledMessage()}
                        </div>
                    )}
                </aside>
                );
            })()}

            {hasItems && (
                <div className="fd-cart-trust">
                    <div className="fd-cart-trust-item">
                        <div className="fd-cart-trust-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="5.5" cy="17.5" r="2.5" />
                                <circle cx="17.5" cy="17.5" r="2.5" />
                                <path d="M3 17h2v-3l3-7h6l3 7v3h2" />
                            </svg>
                        </div>
                        <div>
                            <div className="fd-cart-trust-title">{t('Fast Delivery')}</div>
                            <div className="fd-cart-trust-sub">{t('Quick and safe')}</div>
                        </div>
                    </div>
                    <div className="fd-cart-trust-item">
                        <div className="fd-cart-trust-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                        </div>
                        <div>
                            <div className="fd-cart-trust-title">{t('Secure Payment')}</div>
                            <div className="fd-cart-trust-sub">{t('100% protected')}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Bascket;
